// @flow

import { combineReducers } from 'redux';
import { toggleValueInArray } from '../../../helpers';

import {
  SELECT_DURATION,
  SELECT_MILEAGE,
  SELECT_CLAIMFREE,
  SELECT_PACKAGE,
  SELECT_COLOR,
  SET_ACCESSORIES_FOR_METHOD,
  SET_BUNDLE,
  SET_EXTRA_CONTRACTS_FOR_METHOD,
  TOGGLE_ACCESSORY,
  TOGGLE_EXTRA_CONTRACT,
  TOGGLE_BUNDLE,
} from './constants';

import type {
  SelectDuration,
  SelectPackage,
  SelectMileage,
  SelectClaimFree,
  SelectColor,
  ToggleAccessory,
  SetAccessories,
  SetExtraContracts,
  ToggleExtraContract,
  ToggleBundle,
  ActiveState,
  ActiveExtrasState,
  Ratio,
  SetBundle,
} from './types';

const initialState = {
  ratio: {},
  activeDuration: {},
  activeMileage: {},
  activeClaimFree: {},
  activeAccessories: {},
  activeColor: {},
  activeCarPackage: {},
  activeExtraContracts: {},
  activeBundles: {},
};

const activeDuration = (
  state: ActiveState = initialState.activeDuration,
  action: SelectDuration,
): ActiveState => {
  switch (action.type) {
    case SELECT_DURATION:
      return {
        ...state,
        [action.pdbId]: action.duration.id,
      };
    default:
      return state;
  }
};

const ratio = (
  state: ActiveState = initialState.activeDuration,
  action: SelectDuration,
): Ratio => {
  switch (action.type) {
    case SELECT_DURATION:
      return {
        ...state,
        [action.pdbId]: (36 / parseInt(action.duration.duration, 10)),
      };
    default:
      return state;
  }
};

const activeCarPackage = (
  state: ActiveState = initialState.activeCarPackage,
  action: SelectPackage,
): ActiveState => {
  switch (action.type) {
    case SELECT_PACKAGE: {
      return {
        ...state,
        [action.pdbId]: action.pack,
      };
    }
    default:
      return state;
  }
};

const activeMileage = (
  state: ActiveState = initialState.activeMileage,
  action: SelectMileage,
): ActiveState => {
  switch (action.type) {
    case SELECT_MILEAGE:
      return {
        ...state,
        [action.pdbId]: action.mileage,
      };
    default:
      return state;
  }
};

const activeClaimFree = (
  state: ActiveState = initialState.activeClaimFree,
  action: SelectClaimFree,
): ActiveState => {
  switch (action.type) {
    case SELECT_CLAIMFREE:
      return {
        ...state,
        [action.pdbId]: action.claimfree,
      };
    default:
      return state;
  }
};

const activeColor = (
  state: ActiveState = initialState.activeColor,
  action: SelectColor,
): ActiveState => {
  switch (action.type) {
    case SELECT_COLOR:
      return {
        ...state,
        [action.pdbId]: action.color,
      };
    default:
      return state;
  }
};

const activeAccessories = (
  state: ActiveExtrasState = initialState.activeAccessories,
  action: ToggleAccessory | SetAccessories,
): ActiveExtrasState => {
  switch (action.type) {
    case TOGGLE_ACCESSORY:
      return {
        ...state,
        [action.pdbId]: {
          ...state[action.pdbId],
          [action.method.valueOf()]: toggleValueInArray(
            (state[action.pdbId] && state[action.pdbId][action.method]) || [],
            action.accessory,
          ),
        },
      };
    case SET_ACCESSORIES_FOR_METHOD: {
      return {
        ...state,
        [action.pdbId]: {
          ...state[action.pdbId],
          [action.method.valueOf()]: action.accessories,
        },
      };
    }
    default:
      return state;
  }
};

const activeExtraContracts = (
  state: ActiveExtrasState = initialState.activeExtraContracts,
  action: SetExtraContracts | ToggleExtraContract,
): ActiveExtrasState => {
  switch (action.type) {
    case TOGGLE_EXTRA_CONTRACT:
      return {
        ...state,
        [action.pdbId]: {
          ...state[action.pdbId],
          [action.method.valueOf()]: toggleValueInArray(
            (state[action.pdbId] && state[action.pdbId][action.method]) || [],
            action.extraContract,
          ),
        },
      };
    case SET_EXTRA_CONTRACTS_FOR_METHOD: {
      return {
        ...state,
        [action.pdbId]: {
          ...state[action.pdbId],
          [action.method.valueOf()]: action.extraContracts,
        },
      };
    }
    default:
      return state;
  }
};

const activeBundles = (
  state: ActiveState = initialState.activeBundles,
  action: SetBundle | ToggleBundle,
): ActiveState => {
  switch (action.type) {
    case SET_BUNDLE:
      return {
        ...state,
        [action.pdbId]: action.bundle,
      };
    case TOGGLE_BUNDLE:
      return {
        ...state,
        [action.pdbId]: state[action.pdbId] === action.bundle ? null : action.bundle,
      };
    default:
      return state;
  }
};

const reducers = {
  ratio,
  activeDuration,
  activeColor,
  activeClaimFree,
  activeAccessories,
  activeMileage,
  activeCarPackage,
  activeExtraContracts,
  activeBundles,
};

export default combineReducers<Object, Object>(reducers);
